import * as SelectPrimitive from "@radix-ui/react-select";
import React, { useCallback, useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { renderSelectBody } from "./sharedSelectBody";
import { useCloseOthersOnOpen } from "src/hooks/useCloseOthersOnOpen";
import { SudoButton } from "../SudoButton";
import { optionVariants } from "./shared";

// Type definitions
export type SelectVariant = "primary" | "secondary" | "tertiary" | "secondaryGrey" | "tertiaryGrey";
export type IconStatus = "default" | "success" | "danger";
export type OptionAlignment = "left" | "center";
export type Side = "bottom" | "top" | "right" | "left";
export type Align = "start" | "center" | "end";

export interface BaseOption {
  value: string;
  label: string;
  subLabel?: string;
  tooltipText?: string;
  icon?: {
    label: string;
    src: string;
    status: IconStatus;
  };
  async?: {
    state: string;
    loadingPercentage: number;
    variant?: SelectVariant;
  };
}

export interface OptionGroup {
  isGroup: true;
  groupLabel: string;
  options: BaseOption[];
}

export type Option = BaseOption | OptionGroup;

interface ThreeDotsSelectProps {
  options: Option[];
  label: string;
  value?: string;
  className?: string;
  maxHeight?: string;
  isOpen?: boolean;
  allowCustom?: boolean;
  disabled?: boolean;
  side?: Side;
  align?: Align;
  onChange?: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
  optionAlignment?: OptionAlignment;
  customIcon?: string;
}

// Select body rendering moved to sharedSelectBody.tsx as renderSelectBody

const ThreeDotsSelect = React.forwardRef<HTMLButtonElement, ThreeDotsSelectProps>(
  ({ className, label, options, value, disabled = false, onChange, maxHeight, isOpen = false, onOpenChange, optionAlignment = "center", side = "bottom", align = "start", customIcon }, ref) => {
    const [open, setOpen] = useState<boolean>(isOpen);
    const prevOpenRef = useRef<boolean>(open);
    const [selectedValue, setSelectedValue] = useState<string | undefined>(value);
    const [selectedOption, setSelectedOption] = useState<BaseOption | undefined>(
      options.find((option): option is BaseOption => !("isGroup" in option) && option.value === value) as BaseOption | undefined
    );
    const onChangeRef = useRef(onChange);
    const onOpenChangeRef = useRef(onOpenChange);
    onChangeRef.current = onChange;
    onOpenChangeRef.current = onOpenChange;

    const { announceOpen } = useCloseOthersOnOpen("sudokit:select-open", () => setOpen(false));

    const handleOpenChange = useCallback((nextOpen: boolean): void => {
      setOpen(nextOpen);

      if (onOpenChangeRef.current) {
        onOpenChangeRef.current(nextOpen);
      }

      if (nextOpen) {
        announceOpen();
      } else {
        setSelectedValue(undefined);
      }
    }, [announceOpen]);

    useEffect(() => {
      // When open goes from true→false, wait for the next mouseup to blur.
      if (prevOpenRef.current === true && open === false) {
        const onMouseUp = (): void => {
          if (ref && "current" in ref && ref.current) {
            ref.current.blur();
          }
          window.removeEventListener("mouseup", onMouseUp);
        };
        window.addEventListener("mouseup", onMouseUp);
      }
      prevOpenRef.current = open;
    }, [open, ref]);

    const handleValueChange = useCallback((nextValue: string): void => {
      setSelectedValue(nextValue);

      if (onChangeRef.current) {
        onChangeRef.current(nextValue);
      }
    }, []);

    const selectedStateStyles = (option: BaseOption): string => {
      let styles = "";
      if (option.icon && option.icon.status) {
        styles += optionVariants({ variant: option.icon.status });
        styles += ` justify-start pr-4`;
      } else {
        styles += optionVariants({ variant: "default" });
      }

      return styles;
    };

    useEffect(() => {
      if (!selectedValue) {
        return;
      }

      const option = options.find((opt): opt is BaseOption => !("isGroup" in opt) && opt.value === selectedValue) as BaseOption | undefined;
      if (!option) {
        return;
      }

      setSelectedOption(option);
    }, [selectedValue, options]);

    return (
      <SelectPrimitive.Root
        value={selectedOption ? selectedOption.value : ""}
        onValueChange={handleValueChange}
        disabled={disabled}
        open={open}
        onOpenChange={handleOpenChange}
      >
        <SelectPrimitive.Trigger
          ref={ref}
          className={`sudokit flex items-center h-[40px] font-inter border-0 fill-app-grey-0 [&>path]:fill-app-grey-0 text-base font-medium leading-[130%] border-solid before:rounded-[10px] px-3 py-2 text-left cursor-pointer ${
            className || ""
          }`}
          aria-label={label}
          asChild={true}
        >
          <SudoButton
            variant={"tertiaryGrey"}
            disabled={disabled}
          >
            <SelectPrimitive.Icon>
              <span className="flex items-center justify-center">
                <SVG
                  src={customIcon ?? "/assets/three-dots.svg"}
                  className="h-6 w-6 fill-app-grey-0 [&>path]:fill-app-grey-0"
                />
              </span>
            </SelectPrimitive.Icon>
          </SudoButton>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            avoidCollisions={true}
            position="popper"
            side={side}
            align={align}
            className="z-[2] origin-top animate-flyout bg-app-menu-color shadow-card border border-app-menu-color-hover p-1 rounded-xl overflow-y-auto"
            style={{
              maxHeight: maxHeight ? maxHeight : "var(--radix-select-content-available-height)",
              minWidth: "var(--radix-select-trigger-width)",
            }}
          >
            <SelectPrimitive.Viewport className="bg-app-menu-color">
              {options.map((option) => {
                if ("isGroup" in option && option.isGroup) {
                  return (
                    <React.Fragment key={`group-${option.groupLabel}`}>
                      <SelectPrimitive.Group
                        key={`group-${option.groupLabel}-content`}
                        className="mt-1"
                      >
                        <SelectPrimitive.Label
                          key={`${option.groupLabel}-label`}
                          className="text-left text-app-grey-0 font-inter text-md font-medium px-3 py-1 mb-1"
                        >
                          {option.groupLabel}
                        </SelectPrimitive.Label>
                        {option.options.map((subOption) => (
                          <React.Fragment key={`${option.groupLabel}-${subOption.value}`}>{renderSelectBody(subOption, selectedValue, selectedStateStyles, optionAlignment)}</React.Fragment>
                        ))}
                      </SelectPrimitive.Group>
                      <hr className="border-app-menu-color-hover border-1 border-solid" />
                    </React.Fragment>
                  );
                }

                return renderSelectBody(option as BaseOption, selectedValue, selectedStateStyles, optionAlignment);
              })}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    );
  }
);

ThreeDotsSelect.displayName = "ThreeDotsSelect";

export default ThreeDotsSelect;
